/**
 * One failure, drawn from its row in the failure table (components/Failures/spec.md): a card
 * (the interruption card) when it has a way forward, a line (core/ui/Notice) when it has none.
 * Rows whose form belongs to another surface (player, sign-in card, History) draw nothing here.
 *
 * Each appearance sends one `failure_shown` (plan decision 6): with the action pressed, or
 * `none` when it leaves without one.
 */
import {useCallback, useEffect, useRef} from 'react'
import {Notice} from '@ui/Notice/Notice'
import {quiesTrack} from '@product/quies/utils/analytics'
import {InterruptionCard, type InterruptionCardAction} from '@product/quies/components/Notices/InterruptionCard'
import noticeStyles from '@product/quies/components/Notices/Notices.module.scss'
import {useInterfaceStrings} from '@product/quies/i18n/catalogue'
import {FAILURE_COPY, type FailureCopyKey} from './failureCopy'
import {ACTION_LABEL, actionEmphasis, failureRow, type FailureAction, type FailureCase} from './failures'
import type {FailureHandlers} from './failureHandlers'

const ACTION_ICON: Partial<Record<FailureAction, InterruptionCardAction['icon']>> = {
    'try-again': 'qs-retry',
    'voice-again': 'qs-retry',
    resume: 'qs-play-fill',
    continue: 'qs-continue',
}

export interface FailureProps {
    failure: FailureCase
    handlers: Partial<FailureHandlers>
    /** Cards: focus the primary action on appearing. The page withholds it while audio plays. */
    focusPrimary?: boolean
    /** Lines with a ttl: called when the line clears itself. */
    onClear?: () => void
}

/** The card's actions: the row's, in order, less those with no behaviour on this page yet; `copy` is the
 *  interface language's failure table. */
export function cardActions(
    failure: FailureCase,
    handlers: Partial<FailureHandlers>,
    onPress?: (a: FailureAction) => void,
    copy: Record<FailureCopyKey, string> = FAILURE_COPY,
): InterruptionCardAction[] {
    const available = failureRow(failure).actions.filter((a) => handlers[a])
    return available.map((a, i) => ({
        label: copy[ACTION_LABEL[a]],
        emphasis: actionEmphasis(available, i),
        icon: ACTION_ICON[a],
        target: handlers[a]!,
        onPress: onPress ? () => onPress(a) : undefined,
    }))
}

/** One `failure_shown` per appearance of a drawn failure: `pressed(action)` sends it with the action,
 *  leaving (unmount, page hide) sends `none`. For surfaces that draw a case themselves (a History row). */
export function useFailureShown(failure: FailureCase, drawn: boolean) {
    const sentRef = useRef(false)

    useEffect(() => {
        if (!drawn) return
        sentRef.current = false
        const leave = () => {
            if (sentRef.current) return
            sentRef.current = true
            quiesTrack('failure_shown', {case: failure, action: 'none'})
        }
        window.addEventListener('pagehide', leave)
        return () => {
            window.removeEventListener('pagehide', leave)
            leave()
        }
    }, [failure, drawn])

    return useCallback((action: FailureAction) => {
        if (sentRef.current) return
        sentRef.current = true
        quiesTrack('failure_shown', {case: failure, action})
    }, [failure])
}

export function Failure({failure, handlers, focusPrimary, onClear}: FailureProps) {
    const copy = useInterfaceStrings().t.failure
    const row = failureRow(failure)
    const pressed = useFailureShown(failure, row.form === 'card' || row.form === 'line')
    const message = row.message ? copy[row.message] : ''

    if (row.form === 'line') {
        return (
            <Notice kind={row.severity} ttlMs={row.ttlMs} onClear={onClear} className={noticeStyles.line}>
                {message}
            </Notice>
        )
    }
    if (row.form !== 'card') return null

    return (
        <InterruptionCard
            severity={row.severity}
            title={row.title ? copy[row.title] : undefined}
            message={message}
            actions={cardActions(failure, handlers, pressed, copy)}
            focusPrimary={focusPrimary}
        />
    )
}
